import { DatePicker, Select } from 'antd';
import moment from 'moment';
import 'react-quill-new/dist/quill.snow.css';
import countries from 'world_countries_lists/data/countries/pt/countries.json';

import useAPIQuery from '../../../api/useAPIQuery';
import Form from '../../../components/Form';
import { PublicationFormValues } from './PublicationForm';
import styles from './PublicationInfoForm.module.css';

interface PublicationInfoFormProps {
  linkedinExternalPublication: boolean;
  externalPublication: boolean;
}
export const isDescriptionEmpty = (t: string | undefined) =>
  !t || t?.replaceAll('<br>', '') === '<p></p>';

const PublicationInfoForm = ({
  linkedinExternalPublication,
  externalPublication,
}: PublicationInfoFormProps) => {
  const form = Form.useFormInstance<PublicationFormValues>();

  const countryValue = Form.useWatch('country', form);
  const stateValue = Form.useWatch('state', form);
  const externalPublicationValue = Form.useWatch('externalPublication', form);
  const googleForJobsExternalPublicationValue = Form.useWatch(
    'googleForJobsExternalPublication',
    form
  );
  const netvagasExternalPublicationValue = Form.useWatch('netvagasExternalPublication', form);
  const linkedinExternalPublicationValue = Form.useWatch('linkedinExternalPublication', form);
  const careerjetExternalPublicationValue = Form.useWatch('careerjetExternalPublication', form);
  const joobleExternalPublicationValue = Form.useWatch('joobleExternalPublication', form);

  const states = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/`,
    {
      enabled: !!countryValue,
      refetchOnWindowFocus: false,
    }
  );

  const cities = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/${stateValue}/cities/`,
    {
      enabled: !!countryValue && !!stateValue,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      <Form.Row>
        <Form.Col span={8}>
          <Form.Item
            shouldUpdate={(prevValues, nextValues) =>
              prevValues.linkedinExternalPublication !== nextValues.linkedinExternalPublication ||
              prevValues.careerjetExternalPublication !== nextValues.careerjetExternalPublication ||
              prevValues.joobleExternalPublication !== nextValues.joobleExternalPublication
            }
            noStyle
          >
            {(form) => (
              <Form.Item
                name="country"
                label="País"
                required
                rules={[
                  {
                    required: true,
                    message: 'Por favor, insira o país da vaga',
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder="País"
                  onClear={() => form.setFieldsValue({ state: undefined, city: undefined })}
                  onChange={(value) =>
                    form.setFieldsValue({
                      country: value,
                      state: undefined,
                      city: undefined,
                    })
                  }
                  data-testid="select-country"
                >
                  {countries.map((country) => (
                    <Select.Option key={country.id} value={country.alpha2.toLocaleUpperCase()}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Form.Col>
        <Form.Col span={8}>
          <Form.Item shouldUpdate={(prev, next) => prev.country !== next.country}>
            {(form) => (
              <Form.Item
                label="Estado"
                name="state"
                required={
                  linkedinExternalPublicationValue ||
                  googleForJobsExternalPublicationValue ||
                  netvagasExternalPublicationValue ||
                  careerjetExternalPublicationValue ||
                  joobleExternalPublicationValue
                }
                rules={[
                  {
                    required:
                      linkedinExternalPublicationValue ||
                      googleForJobsExternalPublicationValue ||
                      netvagasExternalPublicationValue ||
                      careerjetExternalPublicationValue ||
                      joobleExternalPublicationValue,
                    message: 'Por favor, insira o estado da vaga',
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder="Estado"
                  disabled={!countryValue}
                  onClear={() => form.setFieldsValue({ city: undefined })}
                  onChange={(value) => form.setFieldsValue({ state: value, city: undefined })}
                  data-testid="select-state"
                >
                  {states.data?.map((state) => (
                    <Select.Option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Form.Col>
        <Form.Col span={8}>
          <Form.Item
            label="Cidade"
            name="city"
            required={
              linkedinExternalPublicationValue ||
              googleForJobsExternalPublicationValue ||
              netvagasExternalPublicationValue ||
              careerjetExternalPublicationValue ||
              joobleExternalPublicationValue
            }
            rules={[
              {
                required:
                  linkedinExternalPublicationValue ||
                  googleForJobsExternalPublicationValue ||
                  netvagasExternalPublicationValue ||
                  careerjetExternalPublicationValue ||
                  joobleExternalPublicationValue,
                message: 'Por favor, insira a cidade da vaga',
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              placeholder="Cidade"
              disabled={!stateValue}
              data-testid="select-city"
            >
              {cities.data?.map((city) => (
                <Select.Option key={city.name} value={city.name}>
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col span={12}>
          <Form.Item
            name="externalPublicationPeriod"
            label="Data de publicação"
            required={externalPublicationValue}
            rules={[
              {
                required: externalPublicationValue,
                message: 'Campo obrigatório para publicação.',
              },
              {
                validator: async (rule, value) => {
                  if (
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment(value[0]).startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error('A data de término deve ser posterior à data de início.')
                    );
                  } else return Promise.resolve();
                },
              },
              {
                validator: async (rule, value) => {
                  const isLinkedinDirty = form.isFieldTouched('linkedinExternalPublication');
                  const isCareerjetDirty = form.isFieldTouched('careerjetExternalPublication');
                  const isJoobleDirty = form.isFieldTouched('joobleExternalPublication');
                  const isExternalPublicationDirty = form.isFieldTouched('externalPublication');

                  if (
                    !externalPublication &&
                    isExternalPublicationDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        'A data de término da publicação deve ser posterior ao dia de hoje.'
                      )
                    );
                  }

                  if (
                    !linkedinExternalPublication &&
                    isLinkedinDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        'A data de término da publicação deve ser posterior ao dia de hoje.'
                      )
                    );
                  }

                  if (
                    isCareerjetDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        'A data de término da publicação deve ser posterior ao dia de hoje.'
                      )
                    );
                  }

                  if (
                    isJoobleDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        'A data de término da publicação deve ser posterior ao dia de hoje.'
                      )
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker.RangePicker
              data-testid="external-publish-date-picker"
              disabledDate={(current) => {
                // Days before today are not allowed
                return current && current < moment().startOf('day');
              }}
              format="L"
              className={styles.externalPublish}
            />
          </Form.Item>
        </Form.Col>
        <Form.Col span={12}>
          <Form.Item name="timezone" label="Fuso horário">
            <Select data-testid="select-gmt">
              <Select.Option value="America/Sao_Paulo">GMT (-03:00) Brasília</Select.Option>
              <Select.Option value="America/Rio_Branco">GMT (-05:00) Rio Branco</Select.Option>
              <Select.Option value="America/Manaus">GMT (-04:00) Manaus</Select.Option>
              <Select.Option value="America/Noronha">
                GMT (-02:00) Fernando de Noronha
              </Select.Option>
            </Select>
          </Form.Item>
        </Form.Col>
      </Form.Row>
    </>
  );
};

export default PublicationInfoForm;
